import { MouseEventHandler, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { Box } from '@mui/material';
import { GridRowModel, GridValidRowModel } from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
    openDrawer,
    setCurrentDrawerId,
    setCurrentEntity,
    setCurrentOperation,
    setCurrentTenantId } from 'common/components/EditDrawer/editDrawerSlice';
import { UserProfileEntityContainerWithAccordeon } from 'common/components/UserProfileEntityContainer/UserProfileEntityContainerWithAccordeon';
import { UserProfilesDataGrid } from 'common/components/UserProfilesDataGrid/UserProfilesDataGrid';
import { Roles } from 'common/constants/roles';
import { Routes } from 'common/constants/routes';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmSortModel } from 'common/hooks/usePalmSortModel';
import { DrawerEntitiesNames,Operations } from 'common/types/TypeDefs';
import { DrawerIDs } from 'common/utils/DrawerIDs';
import { GetDeleteDialogContent } from 'features/aclEntries/AclEntriesDeleteDialogContent';
import { GetDrawerContent } from 'features/aclEntries/AclEntriesDrawerContent';
import { AclEntiriesDeleteDialogProperties, AclEntriesDrawerProperties, AclEntriesPage } from 'features/aclEntries/aclEntriesTypeDefs';
import { IUserProfileTenant } from 'features/userProfileApps/UserProfileApps';
import { selectUserDetails } from 'features/users/usersSlice';

import { _createUserProfileAclEntriesActionDefinitions, _createUserProfileAclEntriesDataGridColumns } from './helpers';
import { useFetchDefaultUserProfileAcls } from './hooks/useFetchDefaultUserProfileAcls';
import { useFetchUserProfileAclsWithLicenseInfo } from './hooks/useFetchUserProfileAclsWithLicenseInfo';

const CONTAINER_INLINE_STYLES = {
    boxShadow: 'unset',
    marginBottom: '1rem',
    position: 'unset',
    backgroundColor: 'secondary.light',
    borderRadius: '4px',
    border: 0.5,
    borderColor: 'info.dark',
    flexBasis: '100%',
    marginTop: '24px',
    height: 'max-content'
};

type UserProfileAclsListProps = {
    tenant: IUserProfileTenant;
}

export function UserProfileAclsList({ tenant }: Readonly<UserProfileAclsListProps>) {
    const params = useParams();
    const dispatch = useAppDispatch();

    const shouldDisableLinkButton = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager ]);
    const [ sortModel, setSortModel ] = usePalmSortModel('resourceTableName');

    const {
        defaultUserProfileAcls,
        processingDefaultUserProfileAcls
    } = useFetchDefaultUserProfileAcls(tenant, params?.userId ?? '');

    const {
        userProfileAclsWithLicense,
        processingUserProfileAclsWithLicense
    } = useFetchUserProfileAclsWithLicenseInfo(tenant, params?.userId ?? '');

    const handleCreateButtonClick: MouseEventHandler<HTMLButtonElement> = event => {
        dispatch(setCurrentEntity(DrawerEntitiesNames.UserAcls));
        dispatch(setCurrentOperation(Operations.Create));
        dispatch(setCurrentTenantId(tenant.id));
        dispatch(setCurrentDrawerId(DrawerIDs.AclEntries));
        dispatch(openDrawer());
        event.stopPropagation();
    };

    const disabledDeleteCallback = (row: GridRowModel) => {
        return shouldDisableLinkButton || Object.hasOwn(row, 'userGroupName');
    };

    const disabledEditCallback = (row: GridRowModel) => {
        return shouldDisableLinkButton || Object.hasOwn(row, 'userGroupName')
        || tenant.isLicenseRequired != true && row.conditions?.length == 0;
    };

    const disabledOpenUserGroupCallback = (row: GridRowModel) => {
        return shouldDisableLinkButton || !Object.hasOwn(row, 'userGroupName');
    };

    const disabledOpenLicenseCallback = () => {
        return shouldDisableLinkButton || !tenant.isLicenseRequired;
    };

    const getLicenseUrl = useCallback((id: string, row?: GridValidRowModel | undefined): string => {
        return `/${Routes.ApplicationSuites}/${tenant.applicationSuiteId}/${Routes.Tenants}/${tenant.id}/${Routes.Licenses}/${row?.acl?.licenseId}/${Routes.AclEntries}`;
    }, []);

    const getUserGroupUrl = useCallback((id: string, row?: GridValidRowModel | undefined): string => {
        return `/${Routes.ApplicationSuites}/${tenant.applicationSuiteId}/${Routes.Tenants}/${tenant.id}/${Routes.UserGroups}/${row?.acl?.usergroupId}/${Routes.AclEntries}`;
    }, []);

    const actionDefinitions = _createUserProfileAclEntriesActionDefinitions(disabledDeleteCallback, disabledEditCallback,
        disabledOpenLicenseCallback, disabledOpenUserGroupCallback, getLicenseUrl, getUserGroupUrl
    );

    const columnDefinitions = _createUserProfileAclEntriesDataGridColumns(tenant.isLicenseRequired);

    const userDetails = useAppSelector(selectUserDetails);

    function GetUserProfileAclDrawer() {
        const drawerProps : AclEntriesDrawerProperties = {
            principalId: params?.userId ?? '',
            principalName: `${userDetails?.firstName ? userDetails.firstName : 'User'} ${userDetails?.lastName ? userDetails.lastName : 'Unknown'}`,
            tenantId: tenant?.id ?? '',
            tenantName: tenant?.name ?? '',
            tenantRequiresLicense: tenant?.isLicenseRequired ?? false,
            aclPage: AclEntriesPage.UserProfile,
            currentUserProfileTenant: tenant
        };
        return GetDrawerContent(drawerProps);
    };

    function GetAclDeleteDialogContent() {
        const dialogProps: AclEntiriesDeleteDialogProperties = {
            aclPage: AclEntriesPage.UserProfile,
            principalId: params?.userId ?? '',
            principalName: `${userDetails?.firstName ? userDetails.firstName : 'User'} ${userDetails?.lastName ? userDetails.lastName : 'Unknown'}`,
            currentUserProfileTenant: tenant
        };
        return GetDeleteDialogContent(dialogProps);
    };

    return (
        <UserProfileEntityContainerWithAccordeon
            title='ACL Entries'
            isCreateButtonDisabled={shouldDisableLinkButton}
            shouldShowCreateButton={true}
            containerInlineStyles={CONTAINER_INLINE_STYLES}
            createButtonHandler={handleCreateButtonClick}
            operationName={Operations.Create}
        >
            <Box sx={{ margin: '0 24px 24px 24px' }}>
                <UserProfilesDataGrid
                    tenant={tenant}
                    rows={tenant.isLicenseRequired ? userProfileAclsWithLicense : defaultUserProfileAcls}
                    actionDefinitions={actionDefinitions}
                    columnDefinitions={columnDefinitions}
                    processing={tenant.isLicenseRequired ? processingUserProfileAclsWithLicense : processingDefaultUserProfileAcls}
                    title='ACL Entries'
                    currentEntityType={DrawerEntitiesNames.UserAcls}
                    noRowsOverlayCustomTitle='ACL Entries'
                    contentFunction={GetUserProfileAclDrawer()}
                    deleteFunction={GetAclDeleteDialogContent()}
                    sortModel={sortModel}
                    clientSortHandler={setSortModel}
                />
            </Box>
        </UserProfileEntityContainerWithAccordeon>
    );
}
